/* assets/cart-features.js */

/**
 * MODULE 1: FREE SHIPPING PROGRESS
 */
(function () {
  'use strict';

  // Grab settings from the window object (defined in theme.liquid)
  const themeSettings = window.themeSettings || {};
  const shippingSettings = themeSettings.shipping || {};

  const CONFIG = {
    thresholds: {
      USD: shippingSettings.threshold_usd || 20000, 
      GLOBAL: shippingSettings.threshold_global || 30000
    },
    api: {
      live: 'https://api.frankfurter.app/latest',
      // Increased to 1.03 so external fallbacks err HIGH, preventing premature unlocking
      safetyBuffer: 1.03 
    },
    selectors: {
      container: '.free-shipping-progress-container',
      barInner: '#progress',
      text: '.progress-text',
      milestone: '.free-shipping-milestone',
      drawer: '#CartDrawer', 
      currencyMeta: 'meta[property="og:price:currency"]',
      subtotalFallback: [
        '.totals__total-value', 
        '.cart-drawer__footer .totals__subtotal', 
        '[data-cart-subtotal]',
        '.cart__subtotal'
      ]
    }
  };

  const state = {
    currency: "USD",
    rate: 1.0,
    isUpdating: false,
    timer: null,
    cachedPct: 0,
    cachedText: '',
    hasCalculatedOnce: false,
    observerLocked: false 
  };

  const DIGITS_ONLY_REGEX = /[^0-9]/g;

  // --- UTILS ---
  
  function formatMoney(amount) {
    return new Intl.NumberFormat("en-US", { 
      style: "currency", 
      currency: state.currency 
    }).format(amount / 100);
  }

  function scrapeCartTotal() {
    const drawer = document.querySelector(CONFIG.selectors.drawer);
    if (!drawer) return null;

    let priceElement = null;
    for (const selector of CONFIG.selectors.subtotalFallback) {
      const el = drawer.querySelector(selector);
      if (el) {
        priceElement = el;
        break;
      }
    }

    if (!priceElement) return null;

    const rawText = priceElement.innerText.trim();
    const cleanString = rawText.replace(DIGITS_ONLY_REGEX, ''); 
    const val = parseInt(cleanString, 10);

    return isNaN(val) ? null : val;
  }

  // --- LOGIC ---

  async function fetchRates() {
    // 1. Try Shopify's native currency tracker first (100% accurate to checkout)
    if (window.Shopify && window.Shopify.currency) {
      state.currency = window.Shopify.currency.active || "USD";
      if (window.Shopify.currency.rate) {
        state.rate = parseFloat(window.Shopify.currency.rate);
        return;
      }
    }

    // 2. Fallback to Meta tag calculation if Shopify object isn't fully ready
    const meta = document.querySelector(CONFIG.selectors.currencyMeta);
    state.currency = meta ? meta.content.substring(0, 3).toUpperCase() : "USD";

    if (state.currency === "USD") { 
      state.rate = 1; 
      return; 
    }

    // 3. Third-party API fallback (with corrected protective safety buffer)
    try {
      const res = await fetch(`${CONFIG.api.live}?from=USD&to=${state.currency}`);
      const data = await res.json();
      if (data.rates && data.rates[state.currency]) {
        state.rate = data.rates[state.currency] * CONFIG.api.safetyBuffer;
        return;
      }
    } catch (e) {
      console.error('Progress Bar API Fallback Error:', e);
    }
    
    // 4. Last resort safety fallback 
    state.rate = state.currency === "CAD" || state.currency === "AUD" ? 1.45 : 1.2;
  }

  function renderVisuals(pct, htmlText) {
    const drawer = document.querySelector(CONFIG.selectors.drawer);
    if (!drawer) return;

    const bar = drawer.querySelector(CONFIG.selectors.barInner);
    const text = drawer.querySelector(CONFIG.selectors.text);
    const milestone = drawer.querySelector(CONFIG.selectors.milestone);

    if (!bar || !text) return;

    state.observerLocked = true;

    requestAnimationFrame(() => {
      bar.style.width = `${pct}%`;
      bar.style.backgroundColor = pct >= 100 ? '#4caf50' : 'black';

      if (text.innerHTML !== htmlText) {
        text.innerHTML = htmlText;
      }

      if (milestone) {
        pct >= 100 ? milestone.classList.add('unlocked') : milestone.classList.remove('unlocked');
      }

      setTimeout(() => { state.observerLocked = false; }, 100);
    });
  }

  function calculateAndRender(cartTotal) {
    const thresholdUSD = state.currency === "USD" ? CONFIG.thresholds.USD : CONFIG.thresholds.GLOBAL;
    const thresholdLocal = thresholdUSD * state.rate;
    const safeTotal = (typeof cartTotal === 'number') ? cartTotal : 0;
    
    const pct = Math.min((safeTotal / thresholdLocal) * 100, 100);
    const remaining = Math.max(0, thresholdLocal - safeTotal);

    let htmlText = '';
    // Added a small 0.5 cent rounding tolerance check to avoid floating point false positives
    if (remaining > 50) {
      htmlText = `You're <b>${formatMoney(remaining)}</b> away from <b>free shipping</b>`;
    } else {
      htmlText = `<b>You've unlocked free shipping!</b>`;
    }

    state.cachedPct = pct;
    state.cachedText = htmlText;
    state.hasCalculatedOnce = true;

    renderVisuals(pct, htmlText);
  }

  // --- HANDLERS ---

  async function handleCartUpdate() {
    if (state.isUpdating) return;
    state.isUpdating = true;
    try {
      const res = await fetch("/cart.js");
      if (res.ok) {
        const data = await res.json();
        calculateAndRender(data.total_price);
      }
    } catch (e) { 
      console.error('Progress Bar Error:', e); 
    } finally { 
      state.isUpdating = false; 
    }
  }

  function debouncedUpdate() {
    clearTimeout(state.timer);
    state.timer = setTimeout(handleCartUpdate, 250);
  }

  async function init() {
    await fetchRates();
    handleCartUpdate(); 

    if (window.PUB_SUB_EVENTS && window.subscribe) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, debouncedUpdate);
    }

    const drawer = document.querySelector(CONFIG.selectors.drawer);
    if (drawer) {
      const observer = new MutationObserver((mutations) => {
        if (state.observerLocked) return;

        let contentChanged = false;
        for (const mutation of mutations) {
            if (mutation.target.id === 'progress' || mutation.target.classList.contains('progress-text')) continue;
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
               contentChanged = true;
               break; 
            }
        }

        if (contentChanged) {
            const visibleTotal = scrapeCartTotal();
            if (visibleTotal !== null) {
                calculateAndRender(visibleTotal);
            } else if (state.hasCalculatedOnce) {
                renderVisuals(state.cachedPct, state.cachedText);
            }
            debouncedUpdate();
        }
      });
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * MODULE 2: CART RECOMMENDATIONS
 * (Pasted directly from your original code - no changes needed for settings here usually)
 */
 (function () {
  'use strict';

  // Shared debounce utility
  function debounce(func, delay) {
    let timer;
    function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    }
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  const getSection = () => document.querySelector('.cart-recommendations');

  /**
   * Updates button opacity based on scroll position.
   */
  function updateButtonState() {
    const slider = document.querySelector('.recommendations-slider');
    const prevButton = document.querySelector('.slider-prev');
    const nextButton = document.querySelector('.slider-next');

    if (!slider || !prevButton || !nextButton) return;

    const slideWidth = slider.offsetWidth;
    if (slideWidth === 0) return; // Hidden

    const scrollLeft = slider.scrollLeft;
    const scrollWidth = slider.scrollWidth;
    const clientWidth = slider.clientWidth;

    // Check boundaries
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    prevButton.style.opacity = isAtStart ? '0.5' : '1';
    prevButton.style.pointerEvents = isAtStart ? 'none' : 'auto';

    nextButton.style.opacity = isAtEnd ? '0.5' : '1';
    nextButton.style.pointerEvents = isAtEnd ? 'none' : 'auto';
  }

  /**
   * Initializes slider visibility and initial state
   */
  function initSlider() {
    const slider = document.querySelector('.recommendations-slider');
    if (!slider) return;

    updateButtonState();

    slider.onscroll = debounce(() => {
      updateButtonState();
    }, 50);
  }

  const debouncedInitSlider = debounce(initSlider, 300);

  /**
   * Handles Next/Prev clicks
   */
  document.addEventListener('click', (event) => {
    const prevBtn = event.target.closest('.slider-prev');
    const nextBtn = event.target.closest('.slider-next');

    if (!prevBtn && !nextBtn) return;

    const slider = document.querySelector('.recommendations-slider');
    if (!slider) return;

    event.preventDefault();

    const slideWidth = slider.offsetWidth;
    const currentScroll = slider.scrollLeft;
    let targetScroll;

    if (prevBtn) {
      targetScroll = currentScroll - slideWidth;
    } else {
      targetScroll = currentScroll + slideWidth;
    }

    slider.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });

    setTimeout(updateButtonState, 350);
  });

  /**
   * Checks if recommendations should be visible based on disabled buttons.
   */
  function checkAndHideRecommendations() {
    const section = getSection();
    if (!section) return;

    const cards = document.querySelectorAll('.recommendation-card');

    if (cards.length === 0) {
      section.style.display = 'none';
      return;
    }

    const allDisabled = Array.from(cards).every((card) => {
      const btn = card.querySelector('.recommendation-add-button');
      return !btn || btn.disabled;
    });

    if (allDisabled) {
      section.style.display = 'none';
    } else if (section.style.display === 'none') {
      // Only show if we confirm cart is NOT empty elsewhere
      // But typically we leave this controlled by updateRecommendations
    }
  }

  // Watch for changes in the container
  const recommendationsContainer = document.querySelector('[data-recommendations-container]');
  if (recommendationsContainer) {
    const observer = new MutationObserver(() => {
      checkAndHideRecommendations();
      debouncedInitSlider();
    });
    observer.observe(recommendationsContainer, { childList: true, subtree: true, attributes: true });
  }

  /**
   * Fetches cart state and updates button status.
   * THIS IS THE CRITICAL LOGIC FOR HIDING WHEN EMPTY
   */
  async function updateRecommendations() {
    const section = getSection();

    try {
      // Force fetch the latest cart state
      const res = await fetch('/cart.js');
      const cart = await res.json();

      // 1. IMMEDIATE CHECK: Is cart empty?
      if (cart.item_count === 0 || cart.items.length === 0) {
        if (section) section.style.display = 'none';
        return; // Stop here, do not process buttons
      }

      // 2. If not empty, calculate buttons
      const cartProductIds = cart.items.map((item) => item.product_id);
      let availableCount = 0;

      document.querySelectorAll('.recommendation-card').forEach((card) => {
        const productId = parseInt(card.getAttribute('data-product-id'), 10);
        const button = card.querySelector('.recommendation-add-button');
        if (!button) return;

        if (cartProductIds.includes(productId)) {
          button.disabled = true;
          button.innerHTML = '<span>Added</span>';
        } else {
          button.disabled = false;
          button.innerHTML = `<span>Add to Cart</span>
          <div class="loading__spinner hidden">
            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
              <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
            </svg>
          </div>`;
          availableCount++;
        }
      });

      // 3. Show/Hide based on availability
      if (section) {
        if (availableCount > 0) {
          section.style.display = '';
          debouncedInitSlider();
        } else {
          section.style.display = 'none';
        }
      }
    } catch (err) {
      console.error('Error fetching cart data:', err);
    }
  }

  const debouncedUpdate = debounce(updateRecommendations, 100);

  /**
   * Adds item to cart via AJAX.
   */
  async function addToCart(variantId, button) {
    const cartDrawer = document.querySelector('cart-drawer');
    const section = getSection();

    button.classList.add('loading');
    button.setAttribute('aria-disabled', true);
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('form_type', 'product');
      formData.append('utf8', '✓');

      // Add sections to re-render if using cart drawer
      if (cartDrawer) {
          const sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description);
      }

      const cartData = await response.json();
      
      if (cartDrawer) {
          cartDrawer.renderContents(cartData);
      }

      if (button) {
        button.disabled = true;
        button.innerHTML = '<span>Added</span>';
      }

      // Hide section immediately if all items are now added
      const enabledButtons = document.querySelectorAll(
        '.recommendation-card .recommendation-add-button:not([disabled])'
      );

      if (enabledButtons.length === 0 && section) {
        section.style.display = 'none';
      }

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-recommendations',
          cartData: cartData,
          variantId: variantId,
        });
      }
      
      // Force update again to be safe
      debouncedUpdate();

    } catch (error) {
      console.error('Error adding to cart:', error.message);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  /**
   * NEW: Monitors all network requests.
   * If any request is made to change/update/clear the cart, we re-check recommendations.
   * This covers clicks, minus buttons, and trash icons.
   */
  function monitorCartRequests() {
    const originalFetch = window.fetch;
    window.fetch = function () {
        const fetchPromise = originalFetch.apply(this, arguments);
        
        fetchPromise.then((response) => {
            // Check if the URL involves cart modification
            if (response.url && (
                response.url.includes('/cart/change') || 
                response.url.includes('/cart/update') || 
                response.url.includes('/cart/clear') ||
                response.url.includes('/cart/add')
            )) {
                // Wait a tiny bit for Shopify to process, then update
                debouncedUpdate();
            }
        });
        
        return fetchPromise;
    };
  }

  /**
   * NEW: Specific observer for the Cart Drawer element
   * In case the theme uses a framework that bypasses standard fetch or updates weirdly.
   */
  function observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const observer = new MutationObserver((mutations) => {
        // If the drawer html updates, re-check cart
        debouncedUpdate();
    });

    observer.observe(cartDrawer, { 
        childList: true, 
        subtree: true,
        attributes: false 
    });
  }

  // --- EVENTS & INIT ---

  document.addEventListener('DOMContentLoaded', () => {
    updateRecommendations();
    monitorCartRequests(); // Initialize the network monitor
    observeCartDrawer();   // Initialize the drawer observer

    window.addEventListener('resize', () => {
      const section = getSection();
      if (section && section.style.display !== 'none') {
        debouncedInitSlider();
      }
    });

    document.addEventListener(
      'change',
      (event) => {
        const variantSelect = event.target.closest('.recommendation-variant-select');
        if (!variantSelect) return;

        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const button = variantSelect
          .closest('.recommendation-details')
          .querySelector('.recommendation-add-button');

        if (button) {
          button.dataset.variantId = selectedOption.value;
          event.stopPropagation();
        }
      },
      true
    );

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('.recommendation-add-button');
      if (!button || !button.closest('.cart-recommendations')) return;

      event.preventDefault();
      if (button.disabled) return;

      let variantId = button.dataset.variantId;
      const select = button.closest('.recommendation-details').querySelector('.recommendation-variant-select');
      if (select) variantId = select.value;

      await addToCart(variantId, button);
    });

    const section = getSection();
    if (section) {
      const closeBtn = section.querySelector('.cart-recommendations-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          section.style.display = 'none';
        });
      }
    }

    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData?.items?.length === 0) {
          const s = getSection();
          if (s) s.style.display = 'none';
        }
        debouncedUpdate();
      });
    }
  });
})();